/**
 * The signed-out screen.
 *
 * The design this is ported from (`modern-erp-login`) shows an email +
 * password form, a "keep me signed in" checkbox and a "forgot password" link,
 * with single sign-on offered underneath as the secondary option. Those are
 * not built here, and deliberately so:
 *
 *   There is no password to collect. Authentication is Entra via MSAL redirect
 *   (auth/msal.ts) — the ERP has never held a credential and there is nothing
 *   for a password field to submit to. A form that looks real and does nothing
 *   is worse than no form.
 *
 *   Even if there were somewhere to send it, a page on erp.pacxa.com asking
 *   for the work password is the exact shape of the phishing page IT tells
 *   people to distrust. The whole value of federated sign-in is that the
 *   password is only ever typed on login.microsoftonline.com.
 *
 * So the layout, tokens, motion and copy are the designer's, unchanged; the
 * credential fields are replaced by the action that is actually real. If local
 * accounts are ever added, the form belongs here and the design has it.
 */
import { useMsal } from '@azure/msal-react';
import { useState, type ComponentType, type CSSProperties, type ReactNode } from 'react';

import { DataNetwork } from './DataNetwork.js';
import { loginRequest } from './msal.js';
import './signin.css';

/** Drawn to the design's icon spec: 24 viewbox, stroke, round caps and joins. */
function Icon({ children, size = 24 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="var(--icon-stroke)"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const AnalyticsIcon = () => (
  <Icon>
    <path d="M4 20V10M10 20V6M16 20v-8M22 20H2" />
  </Icon>
);

const ManagedServicesIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
  </Icon>
);

const ConnectivityIcon = () => (
  <Icon>
    <circle cx="5" cy="18" r="2" />
    <circle cx="19" cy="6" r="2" />
    <circle cx="12" cy="12" r="2" />
    <path d="M6.7 16.7l3.9-3.9M13.4 10.6l3.9-3.9" />
  </Icon>
);

const LockIcon = () => (
  <Icon size={16}>
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </Icon>
);

/** The Microsoft four-square, at its official colours — not a token decision. */
function MicrosoftMark() {
  return (
    <svg viewBox="0 0 21 21" width="18" height="18" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

const PILLARS: Array<{ icon: ComponentType; title: string; body: string }> = [
  {
    icon: AnalyticsIcon,
    title: 'Real-time execution',
    body: 'Operations that run the moment data lands — no overnight batch, no lag.',
  },
  {
    icon: ManagedServicesIcon,
    title: 'AI-driven decisions',
    body: 'Models that read context and recommend the next best action across the business.',
  },
  {
    icon: ConnectivityIcon,
    title: 'Autonomous orchestration',
    body: 'Processes that trigger, route, and resolve themselves end to end.',
  },
];

/** `--reveal-index` staggers the entrance; see .pxsignin__rise in signin.css. */
const rise = (index: number) => ({ '--reveal-index': index }) as CSSProperties;

function ErxIntro() {
  return (
    <div className="pxsignin__pitch">
      <div className="pxsignin__rise" style={rise(0)}>
        <span className="pxsignin__badge">
          <span className="pxsignin__badge-dot" />
          Introducing ERX
        </span>
      </div>

      <div
        className="pxsignin__rise"
        style={{
          ...rise(1),
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <h2 className="pxsignin__display">Beyond ERP. This is ERX.</h2>
        <p className="pxsignin__lead">
          ERX extends ERP with real-time execution, AI-driven decision-making, and autonomous
          process orchestration — so your business doesn&rsquo;t just record what happened, it acts
          on what&rsquo;s next.
        </p>
      </div>

      <ul className="pxsignin__pillars pxsignin__rise" style={rise(2)}>
        {PILLARS.map(({ icon: PillarIcon, title, body }) => (
          <li key={title} className="pxsignin__pillar">
            <span className="pxsignin__pillar-icon">
              <PillarIcon />
            </span>
            <div>
              <p className="pxsignin__pillar-title">{title}</p>
              <p className="pxsignin__pillar-body">{body}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SignInCard() {
  const { instance } = useMsal();
  const [status, setStatus] = useState<'idle' | 'redirecting' | 'failed'>('idle');

  async function signIn() {
    if (status === 'redirecting') return;
    setStatus('redirecting');
    try {
      // Resolves by navigating away. If it returns, the redirect never
      // happened — a blocked popup-less redirect, a misconfigured redirect
      // URI, or MSAL refusing an interaction while one is in progress.
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('[signin] loginRedirect failed', err);
      setStatus('failed');
    }
  }

  const redirecting = status === 'redirecting';

  return (
    <>
      <div className="pxsignin__card-head pxsignin__rise" style={rise(1)}>
        <p className="pxsignin__eyebrow pxsignin__eyebrow--onlight">Pacxa ERX Platform</p>
        <h1 className="pxsignin__card-title">Sign in to your workspace</h1>
        <p className="pxsignin__card-sub">
          Use your Pacxa account. You&rsquo;ll be taken to Microsoft to sign in.
        </p>
      </div>

      {status === 'failed' && (
        <div className="pxsignin__error" role="alert">
          We couldn&rsquo;t start the sign-in. Reload the page and try again; if it keeps happening,
          contact IT.
        </div>
      )}

      <div className="pxsignin__rise" style={rise(2)}>
        <button
          type="button"
          className="pxsignin__button"
          onClick={() => void signIn()}
          disabled={redirecting}
        >
          {redirecting ? (
            <>
              <span className="pxsignin__spinner" style={{ display: 'inline-flex' }}>
                <Icon size={20}>
                  <path d="M12 3a9 9 0 1 0 9 9" />
                </Icon>
              </span>
              Taking you to Microsoft…
            </>
          ) : (
            <>
              <MicrosoftMark />
              Sign in with Microsoft
            </>
          )}
        </button>
      </div>

      <div className="pxsignin__divider" role="separator" aria-hidden="true">
        or
      </div>

      <div className="pxsignin__note pxsignin__rise" style={rise(3)}>
        {/* The design links "Contact your administrator" to a support address.
            There is no IT mailbox recorded anywhere in this repo and every
            other error message in the app says "contact IT" without one, so
            this matches that rather than inventing a destination. Give me the
            address and it becomes a link. */}
        <p>No access yet? Contact IT to be granted a role.</p>
        <p className="pxsignin__note-row">
          <span className="pxsignin__note-icon">
            <LockIcon />
          </span>
          Your password is only ever entered on Microsoft&rsquo;s sign-in page
        </p>
      </div>
    </>
  );
}

export function SignInPage() {
  return (
    <main className="pxsignin">
      <DataNetwork />

      <span className="pxsignin__rule" aria-hidden="true">
        <span className="pxsignin__sheen" />
      </span>

      <header className="pxsignin__header">
        <span className="pxsignin__wordmark">Pacxa</span>
        <span className="pxsignin__eyebrow">ERX Platform</span>
      </header>

      <div className="pxsignin__main">
        <div className="pxsignin__intro">
          <ErxIntro />
        </div>
        <div className="pxsignin__card">
          <SignInCard />
        </div>
      </div>

      {/* The design's footer carries Terms / Privacy / Help links. They are
          dropped rather than pointed at guessed URLs — this is an internal
          tool behind Entra, and a link to a page that does not exist is worse
          than no link. Add them here when the destinations are known. */}
      <footer className="pxsignin__footer">
        <span>© Pacxa. All rights reserved.</span>
        <span className="pxsignin__eyebrow">Internal use only</span>
      </footer>
    </main>
  );
}
